import { useMemo, useState } from "react";

import * as S from "./styled";

type TaxBand = "basic" | "higher" | "additional";

const PLATFORM_FEE = 0.125;
const NURSERY_CONTRIBUTION = 0.125;

const TAX_BANDS: Record<TaxBand, { tax: number; ni: number }> = {
  basic: { tax: 0.2, ni: 0.08 },
  higher: { tax: 0.4, ni: 0.02 },
  additional: { tax: 0.45, ni: 0.02 },
};

const taxBandOptions: Array<{
  value: TaxBand;
  label: string;
  description: string;
}> = [
  { value: "basic", label: "Basic rate", description: "up to £50,270" },
  {
    value: "higher",
    label: "Higher rate",
    description: "£50,271–£125,140",
  },
  {
    value: "additional",
    label: "Additional rate",
    description: "over £125,140",
  },
];

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => gbp.format(Math.round(value));

const calculateSavings = (bill: number, bandKey: TaxBand) => {
  const band = TAX_BANDS[bandKey];
  const relief = band.tax + band.ni;
  const sacrifice = bill * (1 + PLATFORM_FEE);
  const netCost = sacrifice * (1 - relief);
  const saveMonth = bill - netCost;
  const fundMonth = bill * NURSERY_CONTRIBUTION;

  return {
    saveMonth,
    saveYear: saveMonth * 12,
    netCost,
    fundMonth,
    fundYear: fundMonth * 12,
  };
};

export const NurseryCalculator = () => {
  const [bill, setBill] = useState(1200);
  const [taxBand, setTaxBand] = useState<TaxBand>("higher");
  const [families, setFamilies] = useState(5);
  const nurseryName = useMemo(() => {
    const name = new URLSearchParams(window.location.search).get("nursery");

    return name?.trim() || "your nursery";
  }, []);
  const savings = calculateSavings(bill, taxBand);
  const payPercentage = Math.max(
    20,
    Math.min(88, (savings.netCost / bill) * 100)
  );

  return (
    <S.Page>
      <S.Calculator>
        <S.Header>
          <S.Eyebrow>Workplace nursery scheme</S.Eyebrow>
          <S.Title>How much could you save on nursery fees?</S.Title>
        </S.Header>

        <S.Body>
          <S.Inputs>
            <S.Field>
              <S.FieldLabel htmlFor="nursery-bill">
                <span>Your monthly nursery bill</span>
                <S.FieldValue>{formatCurrency(bill)}</S.FieldValue>
              </S.FieldLabel>
              <S.Range
                id="nursery-bill"
                type="range"
                min="200"
                max="3500"
                step="25"
                value={bill}
                aria-label="Monthly nursery bill in pounds"
                onChange={(event) => setBill(Number(event.target.value))}
              />
              <S.RangeHints>
                <span>£200</span>
                <span>£3,500</span>
              </S.RangeHints>
            </S.Field>

            <S.Fieldset>
              <S.FieldLegend>Your income tax band</S.FieldLegend>
              <S.Pills role="group" aria-label="Income tax band">
                {taxBandOptions.map((option) => (
                  <S.Pill
                    type="button"
                    aria-pressed={taxBand === option.value}
                    key={option.value}
                    onClick={() => setTaxBand(option.value)}
                  >
                    {option.label}
                    <S.PillSub>{option.description}</S.PillSub>
                  </S.Pill>
                ))}
              </S.Pills>
            </S.Fieldset>
          </S.Inputs>

          <S.Results aria-live="polite">
            <S.Card $variant="parent">
              <S.ParentGrid>
                <div>
                  <S.CardLabel>You&apos;d save</S.CardLabel>
                  <S.CardValue>
                    {formatCurrency(savings.saveMonth)}
                    <S.CardPer> / month</S.CardPer>
                  </S.CardValue>
                  <S.Split>
                    <S.SplitBar aria-hidden="true">
                      <S.SplitSegment
                        $variant="pay"
                        style={{ width: `${payPercentage}%` }}
                      >
                        You pay
                      </S.SplitSegment>
                      <S.SplitSegment
                        $variant="save"
                        style={{ width: `${100 - payPercentage}%` }}
                      >
                        Saved
                      </S.SplitSegment>
                    </S.SplitBar>
                    <S.SplitLegend>
                      <span>
                        You pay{" "}
                        <strong>{formatCurrency(savings.netCost)}</strong>
                      </span>
                      <span>
                        instead of <strong>{formatCurrency(bill)}</strong>
                      </span>
                    </S.SplitLegend>
                  </S.Split>
                </div>
                <S.ParentYear>
                  <S.CardLabel>Saving over a year</S.CardLabel>
                  <S.CardValue>{formatCurrency(savings.saveYear)}</S.CardValue>
                </S.ParentYear>
              </S.ParentGrid>
            </S.Card>

            <S.Card $variant="nursery">
              <S.CardLabel>{nurseryName} receives</S.CardLabel>
              <S.CardValue>
                {formatCurrency(savings.fundYear)}
                <S.CardPer> / year in extra funding</S.CardPer>
              </S.CardValue>
              <S.CardNote>
                That&apos;s <strong>{formatCurrency(savings.fundMonth)}</strong>{" "}
                a month of additional funding for the nursery — paid by your
                employer, not you. Your bill stays the same; the nursery simply
                receives more. And it doesn&apos;t cost your employer anything
                either — it&apos;s funded by the National Insurance they save.
              </S.CardNote>
            </S.Card>

            <S.Card $variant="scale">
              <S.CardLabel>And it grows as more families join</S.CardLabel>
              <S.ScaleControl>
                <S.ScaleFamilies htmlFor="nursery-families">
                  <span>Families at {nurseryName} using the scheme</span>
                  <S.ScaleCount>{families}</S.ScaleCount>
                </S.ScaleFamilies>
                <S.ScaleRange
                  id="nursery-families"
                  type="range"
                  min="1"
                  max="40"
                  step="1"
                  value={families}
                  aria-label="Number of families using the scheme"
                  onChange={(event) => setFamilies(Number(event.target.value))}
                />
                <S.RangeHints>
                  <span>1</span>
                  <span>40</span>
                </S.RangeHints>
              </S.ScaleControl>
              <S.CardValue>
                {formatCurrency(savings.fundYear * families)}
                <S.CardPer> / year</S.CardPer>
              </S.CardValue>
              <S.CardNote>
                Based on the monthly bill above. The more families who use the
                scheme, the more funding the nursery receives — so it&apos;s
                worth letting other parents know.
              </S.CardNote>
            </S.Card>
          </S.Results>
        </S.Body>

        <S.Footer>
          <S.FooterNote>
            Savings are estimates based on 2026/27 income tax and National
            Insurance rates for England, Wales and Northern Ireland, and include
            Gogeta&apos;s platform fee. Figures are indicative only and depend
            on your individual circumstances. Nursery funding shown is the
            direct additional financial contribution nurseries receive through
            the workplace nursery partnership.
          </S.FooterNote>
          <S.FooterBrand
            href="https://gogeta.bike/nursery?utm_source=nursery-partner&utm_medium=calculator"
            target="_blank"
            rel="noreferrer"
          >
            Powered by <span>Gogeta</span>
          </S.FooterBrand>
        </S.Footer>
      </S.Calculator>
    </S.Page>
  );
};
